import convertDataWithTrendsToAnalyticsData from 'common/src/modules/analytics/utils/convertDataWithTrendsToAnalyticsData';
import getRemoteMetricKey from 'common/src/modules/analytics/utils/getRemoteMetricKey';
import {AvailableDimension} from 'common/src/modules/campaigns/store/insightsPage/insightsPage.types';
import {DashboardWidgetType} from 'common/src/modules/dashboards/dashboards.types';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {TimezoneObject} from 'common/src/modules/timezones/timezones.types';
import forceTimeZone from 'common/src/modules/timezones/utils/forceTimeZone';
import {CLStore} from 'common/src/redux/store';
import _find from 'lodash/find';
import _map from 'lodash/map';
import _uniq from 'lodash/uniq';
import {DateTime} from 'luxon';
import analyticsApi from '../../../../analytics.api';
import {
  AnalyticsData,
  AnalyticsDataWithTrends,
  DimensionValue,
  MetricValue,
} from '../../../../analytics.types';
import getRemoteDimensionKey from '../../../../utils/getRemoteDimensionKey';
import {copilotAnalyticsTableWidgetInputSchema} from '../../../copilotAnalytics.schemas';
import {AccountAnalyticsToolName} from '../accountAnalyticsAgent.enums';
import partitionByEventType from '../utils/partitionByEventType';

function accountAnalyticsTableDataTool(store: CLStore) {
  function handleNoAnalyticsData() {
    return toolResult('Sorry data is unavailable, please try again later');
  }

  async function execute({
    input: {
      dimensions: aiDimensions,
      metrics: aiMetrics,
      startDate,
      endDate,
      filter,
      eventType,
      withTrends = false,
    },
  }: ToolExecuteParams<typeof copilotAnalyticsTableWidgetInputSchema>): Promise<ToolResult> {
    const {
      dashboardPage: {
        startDate: startDateFromStore,
        endDate: endDateFromStore,
        timezone,
        metricsAndDimensionsOptions,
        filters,
        timezoneOptions,
      },
    } = store.getState();

    const timezoneObject = _find(timezoneOptions, {
      abbr: timezone,
    }) as TimezoneObject;

    const dateRange = {
      startDate: forceTimeZone(
        startDate ? DateTime.fromISO(startDate).toUTC() : DateTime.fromISO(startDateFromStore),
        0,
      ),

      endDate: forceTimeZone(
        endDate ? DateTime.fromISO(endDate).toUTC() : DateTime.fromISO(endDateFromStore),
        0,
      ).endOf('day'),
    };

    const metrics = _map(aiMetrics, (metric) =>
      getRemoteMetricKey(metricsAndDimensionsOptions.metrics, metric),
    ) as MetricValue[];

    const [dimensionsByEventType] = partitionByEventType(
      metricsAndDimensionsOptions.dimensions,
      eventType,
    ) as unknown as AvailableDimension[][];

    const dimensions = _uniq(
      _map(
        aiDimensions,
        (dimension) =>
          getRemoteDimensionKey<DimensionValue>(dimensionsByEventType, dimension) ??
          DimensionValue.Date,
      ),
    );

    const filterKey = getRemoteDimensionKey<DimensionValue>(
      filters as unknown as AvailableDimension[],
      filter?.dimension,
    );

    const allFilters = [
      ...(filterKey
        ? [
            {
              filterBy: filterKey,
              allValues: filter?.values ?? [],
            },
          ]
        : []),
    ];

    try {
      const data = await analyticsApi.getDataForDashboard<AnalyticsData | AnalyticsDataWithTrends>({
        dateRange,
        metrics,
        dimensions,
        filters: allFilters,
        selectedTimezone: timezoneObject,
        withTrends: withTrends ?? false,
        eventType,
      });

      const analyticsData = withTrends
        ? convertDataWithTrendsToAnalyticsData(data as AnalyticsDataWithTrends[])
        : data;

      return toolResult(
        JSON.stringify({
          analyticsData,
          withTrends,
          eventType,
          type: DashboardWidgetType.MULTI_TABLE,
        }),
      );
    } catch (error) {
      return handleNoAnalyticsData();
    }
  }

  return tool({
    name: AccountAnalyticsToolName.AccountAnalyticsTableData,
    displayName: 'Loading Analytics Data',
    description: `Retrieves analytics data by multiple metrics and dimensions as ${DashboardWidgetType.MULTI_TABLE} widget`,
    execute,
    inputSchema: copilotAnalyticsTableWidgetInputSchema,
  });
}

export default accountAnalyticsTableDataTool;
